import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn.ts";

interface ModalProps {
  open: boolean; onClose: () => void; title?: string; description?: string;
  children: ReactNode; footer?: ReactNode; size?: "sm" | "md" | "lg";
}

const SIZES = { sm: "max-w-md", md: "max-w-2xl", lg: "max-w-4xl" };

export function Modal({ open, onClose, title, description, children, footer, size = "md" }: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const h = () => onClose();
    d.addEventListener("close", h);
    return () => d.removeEventListener("close", h);
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className={cn("rounded-xl bg-white text-surface-900 backdrop:bg-black/40 w-full p-0 shadow-xl", SIZES[size])}
      onClick={(e) => { if (e.target === dialogRef.current) onClose(); }}
    >
      <div className="flex flex-col">
        {(title || description) && (
          <header className="flex items-start justify-between border-b border-surface-100 px-5 py-3.5">
            <div>
              {title && <h2 className="text-[15px] font-semibold text-surface-900">{title}</h2>}
              {description && <p className="mt-0.5 text-[12px] text-surface-400">{description}</p>}
            </div>
            <button type="button" onClick={onClose} className="rounded-lg p-1 text-surface-400 hover:bg-surface-100 hover:text-surface-600" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </header>
        )}
        <div className="px-5 py-4">{children}</div>
        {footer && <footer className="flex items-center justify-end gap-2 border-t border-surface-100 px-5 py-3.5">{footer}</footer>}
      </div>
    </dialog>
  );
}
